// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: a0b1a719-9e50-4dfc-befd-1f42c4a63f09

import React, { useMemo } from 'react';
import ReactFlow, { Background, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';

// Directional data-flow map for an application's integrations.
// Each integration is a hop chain:  [app] --protocol/port--> [via engine] --> [target]
// e.g. Airstrip One --HL7/TCP 443--> Corepoint (Interface Engine) --> Epic
export default function IntegrationFlow({ appName, integrations = [] }) {
  const { nodes, edges } = useMemo(() => {
    const nodes = [];
    const edges = [];
    const nodeIds = new Map();
    const ensure = (label, kind, x, y) => {
      if (nodeIds.has(label)) return nodeIds.get(label);
      const id = 'n' + nodeIds.size;
      nodeIds.set(label, id);
      const style =
        kind === 'app'
          ? { background: '#0B5FFF', color: '#fff', border: 'none' }
          : kind === 'engine'
          ? { background: '#fff3e0', border: '2px solid #EF6C00', color: '#8a5a00' }
          : { background: '#fff', border: '2px solid #283593', color: '#283593' };
      nodes.push({ id, position: { x, y }, data: { label }, style: { ...style, borderRadius: 8, width: 170, fontSize: 11, fontWeight: 600 } });
      return id;
    };
    const appId = ensure(appName, 'app', 0, Math.max(0, (integrations.length - 1) * 45));
    const edge = (a, b, label, dir) => {
      const forward = dir !== 'inbound';
      edges.push({
        id: `${a}-${b}-${edges.length}`,
        source: forward ? a : b,
        target: forward ? b : a,
        label,
        labelStyle: { fontSize: 9, fill: '#555' },
        labelBgStyle: { fill: '#fff', fillOpacity: 0.85 },
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { stroke: dir === 'bidirectional' ? '#2E7D32' : '#888' },
        markerStart: dir === 'bidirectional' ? { type: MarkerType.ArrowClosed } : undefined,
      });
    };
    integrations.forEach((it, i) => {
      const y = i * 90;
      const portLabel = [it.protocol, it.port].filter(Boolean).join(' · ');
      if (it.via) {
        const viaId = ensure(it.via, 'engine', 240, y);
        const tgtId = ensure(it.to, 'target', 490, y);
        edge(appId, viaId, portLabel, it.direction);
        edge(viaId, tgtId, it.dataType || '', it.direction);
      } else {
        const tgtId = ensure(it.to, 'target', 490, y);
        edge(appId, tgtId, portLabel, it.direction);
      }
    });
    return { nodes, edges };
  }, [appName, integrations]);

  if (!integrations.length) {
    return <div style={{ color: '#aaa', fontSize: 13, padding: '8px 0' }}>No integrations captured yet.</div>;
  }

  return (
    <Box>
      <Box sx={{ height: Math.max(180, integrations.length * 90 + 40), width: '100%', mb: 2, border: '1px solid #eee', borderRadius: 1 }}>
        <ReactFlow nodes={nodes} edges={edges} fitView nodesDraggable={false}
          zoomOnScroll={false} panOnScroll={false} preventScrolling={false}
          proOptions={{ hideAttribution: true }}>
          <Background gap={16} color="#f0f0f0" />
        </ReactFlow>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main', fontSize: 12 } }}>
            <TableCell>Connects To</TableCell>
            <TableCell>Via (Engine)</TableCell>
            <TableCell>Protocol</TableCell>
            <TableCell>Port(s)</TableCell>
            <TableCell>Direction</TableCell>
            <TableCell>Data</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {integrations.map((it, i) => (
            <TableRow key={i}>
              <TableCell><b>{it.to}</b></TableCell>
              <TableCell>{it.via || '—'}</TableCell>
              <TableCell>{it.protocol || '—'}</TableCell>
              <TableCell>{it.port ? <Chip size="small" label={it.port} sx={{ bgcolor: '#e8f0fe' }} /> : '—'}</TableCell>
              <TableCell>{it.direction || 'outbound'}</TableCell>
              <TableCell>{it.dataType || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
